import styles from "~/sass/organisms/Article/Article.module.scss";
import Head from "~/components/Meta/Head";
import ArticleAuthor from "~/components/molecules/Article/ArticleAuthor.js";
import Script from "next/script";

export default function Article({}) {
  const content = () => {
    return (
      <>
        <h1 className={styles["Article__heading"]}>
          An Accessibility Checklist to Modify Your Home
        </h1>
        <ArticleAuthor />
        <p>
          Home modifications can improve quality of life, reduce the risk of
          falls and injuries, and increase independence for people with
          disabilities and seniors. They can also help caregivers by making care
          and support more manageable for their loved ones. Adaptions to a
          living space can make it more accessible, safe, and comfortable for
          people with mobility limitations. These modifications can range from
          simple changes like adding grab bars or slip-resistant flooring, to
          more extensive renovations like widening doorways, installing
          wheelchair ramps, or creating accessible bathrooms.
        </p>
        <p>
          Modification can mean many different things and different options are
          more suited to a specific situation than others. Take a look at
          possibilities room by room to decide which might be most helpful to
          your life and limitations.
        </p>
        <h2 className={styles["Article__sub-heading"]}>
          <b>Outdoor Modifications</b>
        </h2>
        <p>
          The outdoors and entrances to your home are crucial spaces for
          modification. You need to make sure all residents can get in and out
          of the house safely and have access to outbuildings.
        </p>
        <h3 className={styles["Article__sub-sub-heading"]}>
          <b>Garage</b>
        </h3>
        <p>
          &bull; Standard garage doors are typically 9 feet wide by 7 feet high.
          Widening the doorways to 10 or 12 feet can provide additional
          clearance for people who use wheelchairs or walkers.
        </p>
        <ul>
          <li>
            <p>
              Installations of sensors at the garage doors are essential since
              some people may not move as fast others. Sensors will ensure that
              the door won't shut until the area is clear and safe.
            </p>
          </li>
        </ul>
        <h3 className={styles["Article__sub-sub-heading"]}>
          <b>Parking Spaces</b>
        </h3>
        <ul>
          <li>
            <p>
              Accessible parking spaces should be 8 feet wide to accommodate a
              van with a wheelchair lift or side-entry ramp. Non-van-accessible
              parking spaces should be at least 7 feet wide.
            </p>
          </li>
        </ul>
        <h3 className={styles["Article__sub-sub-heading"]}>
          <b>Walkways and Ramps</b>
        </h3>
        <ul>
          <li>
            <p>
              Ramps should have a slope of no more than one inch for every 12
              inches of length to ensure safe and comfortable access. A gradient
              of one inch for every 20 inches may be used for short ramps less
              than 6 feet long.
            </p>
          </li>
          <li>
            <p>
              Ramps should have edge protection, such as a curb or handrail, to
              prevent wheelchair wheels from slipping off the edge of the ramp.
            </p>
          </li>
          <li>
            <p>
              Landings should be provided at the top and bottom of ramps and
              where ramps change direction. Docks should be at least 60 inches
              long and as wide as the ramp leading to them.
            </p>
          </li>
          <li>
            <p>
              Handrails should be provided on both sides of ramps and walkways.
              Handrails should be between 1.25 and 1.5 inches in diameter and 34
              and 38 inches above the ramp or walkway surface.
            </p>
          </li>
        </ul>
        <h3 className={styles["Article__sub-sub-heading"]}>
          <b>Doorbells and Mailboxes</b>
        </h3>
        <ul>
          <li>
            <p>
              A doorbell should be located at a height that can be reached by
              individuals who use wheelchairs or have limited mobility. In
              addition, a wireless or remote doorbell can be helpful for
              individuals who have difficulty answering the door.
            </p>
          </li>
          <li>
            <p>
              The mailbox should be located within a reach distance of no more
              than 24 inches from the front edge of the sidewalk or pathway
              leading to the mailbox.
            </p>
          </li>
        </ul>
        <h3 className={styles["Article__sub-sub-heading"]}>
          <b>Front Door and Entrance Modifications</b>
        </h3>
        <ul>
          <li>
            <p>
              The front door should be at least 32 inches wide for wheelchair
              access. A minimum width of 36 inches is recommended to provide
              additional space for turning and maneuvering.
            </p>
          </li>
          <li>
            <p>
              The threshold should be no higher than 0.5 inches to allow for
              easy entry and exit for individuals using wheelchairs or mobility
              devices. A threshold ramp or a flush threshold can be installed to
              eliminate the height difference.
            </p>
          </li>
          <li>
            <p>
              The door handle should be located at a height that can be easily
              reached by individuals who use wheelchairs or have limited
              mobility. A lever-style door handle is easier to operate than a
              doorknob for individuals with limited hand strength or dexterity.
            </p>
          </li>
          <li>
            <p>
              The front door should have a full-length sidelight or double
              peephole for visibility.
            </p>
          </li>
        </ul>
        <h2>
          <b>Bedroom Modifications</b>
        </h2>
        <p>
          A good night's sleep is essential to our health and well-being. A
          bedroom should be the most comfortable place in the{" "}
          <a href="https://www.realestateagents.com/real-estate-agents/tampa-fl">
            home
          </a>
          . There are many ways to make sure those with limited mobility can
          have a restful bedroom with no frustrations in moving around.
        </p>
        <ul>
          <li>
            <p>
              The bed height should be appropriate for the individual's needs.
              For individuals using a wheelchair or mobility device, a lower bed
              height of around 20 to 23 inches can be helpful. For individuals
              with difficulty getting in and out of bed, a higher bed height of
              approximately 28 to 30 inches can be beneficial.
            </p>
          </li>
          <li>
            <p>
              A bed rail can be installed to provide support and prevent falls.
              The rail should be sturdy and installed at a height appropriate
              for the individual's needs.
            </p>
          </li>
          <li>
            <p>
              A lift system can be installed to assist individuals with limited
              mobility in and out of bed. The lift system can be manual or
              electric and should be appropriate for the individual's needs.
            </p>
          </li>
        </ul>
        <h3 className={styles["Article__sub-sub-heading"]}>
          <b>Closet</b>
        </h3>
        <ul>
          <li>
            <p>
              Closet rods should be installed at a height the individual can
              easily reach, which may require lowering the rod height. A lower
              closet rod can be helpful for individuals who have limited
              mobility.
            </p>
          </li>
          <li>
            <p>
              Pull-out shelves can be installed to make it easier for
              individuals with limited mobility to access their clothing and
              belongings. These shelves can be pulled out and lowered to a
              convenient height for easy access.
            </p>
          </li>
          <li>
            <p>
              The closet floor should be slip-resistant and provide a smooth
              surface for easy mobility. A low-pile carpet or hardwood floor can
              be helpful.
            </p>
          </li>
        </ul>
        <h2>
          <b>Bathroom Modifications</b>
        </h2>
        <p>
          The bathroom is among the areas that are prone to accidents.
          Therefore, bathroom modifications should be prioritized to ensure the
          safety of our loved ones who have difficulties moving around. These
          are some of the changes that should be considered for adaptation in
          the bathroom:
        </p>
        <ul>
          <li>
            <p>
              Doors should be wide enough to accommodate a wheelchair or
              mobility device. The recommended minimum width for a door is 36
              inches.
            </p>
          </li>
          <li>
            <p>
              An emergency alarm system should be installed to ensure that
              individuals with disabilities can call for help if needed.
            </p>
          </li>
          <li>
            <p>
              A roll-in shower allows easy access for individuals using a
              wheelchair or mobility device. The shower should have grab bars, a
              shower seat, and a handheld showerhead.
            </p>
          </li>
        </ul>
        <h3 className={styles["Article__sub-sub-heading"]}>
          <b>Toilet</b>
        </h3>
        <ul>
          <li>
            <p>
              Grab bars should be installed near the toilet to support and
              stabilize individuals with mobility impairments.
            </p>
          </li>
          <li>
            <p>
              An accessible toilet should have a higher seat, grab bars, and
              ample space to accommodate a wheelchair.
            </p>
          </li>
          <li>
            <p>
              A toilet seat riser can help individuals with mobility impairments
              to use the toilet more easily.
            </p>
          </li>
        </ul>
        <h3 className={styles["Article__sub-sub-heading"]}>
          <b>Sink</b>
        </h3>
        <ul>
          <li>
            <p>
              The sink should be no higher than 34 inches from the floor, and
              the recommended height is between 28 and 34 inches. The depth of
              the sink should also be at most 6 1/2 inches to provide adequate
              knee clearance.
            </p>
          </li>
          <li>
            <p>
              Lever handles should be installed for hot and cold water faucets,
              with a clearance of at least 2 1/2 inches between the handle and
              the backsplash. In addition, the handles should be not more than
              48 inches above the floor.
            </p>
          </li>
          <li>
            <p>
              A soap dispenser should be installed within reach of the sink
              user, with a height of no more than 48 inches above the floor.
            </p>
          </li>
        </ul>
        <h3 className={styles["Article__sub-sub-heading"]}>
          <b>Shower</b>
        </h3>
        <ul>
          <li>
            <p>
              A shower seat can provide a place to sit while showering, making
              it easier for individuals with limited mobility or balance issues
              to use the shower.
            </p>
          </li>
          <li>
            <p>
              The shower floor should be slip-resistant to prevent falls and
              slips.
            </p>
          </li>
          <li>
            <p>
              Shower controls should be installed no higher than 48 inches above
              the floor to make them accessible for individuals who use a
              wheelchair.
            </p>
          </li>
        </ul>
        <h3 className={styles["Article__sub-sub-heading"]}>
          <b>Bathtub</b>
        </h3>
        <ul>
          <li>
            <p>
              A bathtub lift should be able to lower an individual in and out of
              the bathtub safely and comfortably. The lift should support a
              weight of at least 300 pounds, and the seat should be 17 inches
              wide and 14 inches deep.
            </p>
          </li>
          <li>
            <p>
              Grab bars should be installed on the wall next to the bathtub,
              with a height of no more than 48 inches above the floor. The grab
              bars should be at least 1 1/4 inches in diameter, and there should
              be a clearance of at least 1 1/2 inches between the grab bar and
              the wall.
            </p>
          </li>
          <li>
            <p>
              The bathtub height should be lowered to make it easier for
              individuals who use a wheelchair to transfer in and out of the
              tub. The recommended height is no more than 17 inches from the
              floor.
            </p>
          </li>
        </ul>
        <h2>
          <b>Kitchen Modifications</b>
        </h2>
        <p>
          It's possible for many people of varying abilities to cook and get
          full use out of their kitchen to make healthy and comforting meals. A
          few modifications will accommodate people of all kinds of abilities.
        </p>
        <h3 className={styles["Article__sub-sub-heading"]}>
          <b>Cabinets</b>
        </h3>
        <ul>
          <li>
            <p>
              The height of the cabinets should be lowered to make it easier for
              individuals who use a wheelchair or have limited reach to access
              the contents. The recommended size is at most, 54 inches above the
              floor.
            </p>
          </li>
          <li>
            <p>
              Pull-out shelves or drawers can be added to lower cabinets to make
              it easier for individuals to reach items at the back of the
              cabinet. The frames should extend fully out of the cabinet, and
              the weight capacity should be at least 100 pounds.
            </p>
          </li>
          <li>
            <p>
              Cabinet handles should be easy to grasp and pull, sized at least 1
              1/4 inches in diameter. Handles with a loop or D-shape can be
              easier for individuals with limited grip strength.
            </p>
          </li>
        </ul>
        <h3 className={styles["Article__sub-sub-heading"]}>
          <b>Countertops and Sinks</b>
        </h3>
        <ul>
          <li>
            <p>
              A shallow sink can be installed to make it easier for individuals
              with limited reach to access the sink. The sink should have a
              maximum depth of 6 1/2 inches.
            </p>
          </li>
          <li>
            <p>
              A pull-out spray faucet can be installed to make it easier for
              individuals to reach the water source and control the water flow.
            </p>
          </li>
          <li>
            <p>
              The sink should have a clear space underneath it to allow
              individuals who use a wheelchair to roll their legs under the
              sink. The recommended height for the clearance is at least 27
              inches from the floor.
            </p>
          </li>
        </ul>
        <h3 className={styles["Article__sub-sub-heading"]}>
          <b>Appliances</b>
        </h3>
        <ul>
          <li>
            <p>
              Side-opening ovens can be installed to make it easier for
              individuals with limited mobility or access to the stove. In
              addition, the oven should have a clear floor space in front of it
              to allow individuals who use a wheelchair to access the range more
              easily.
            </p>
          </li>
          <li>
            <p>
              Cooktops with front controls can be installed to make it easier
              for individuals with limited reach or mobility to use the cooktop.
              Further, the cooktop should have a clear floor space in front of
              it to allow individuals who use a wheelchair to access the cooktop
              more easily.
            </p>
          </li>
          <li>
            <p>
              Dishwasher drawers can be installed to make it easier for
              individuals with limited reach or mobility to load and unload the
              dishwasher.
            </p>
          </li>
        </ul>
        <h2>
          <b>More Information</b>
        </h2>
        <ul>
          <li>
            <p>
              <a href="https://hiehelpcenter.org/caregiving/considerations/home-modifications/#:~:text=Modifications%20can%20change%20the%20physical,bathrooms%20and%20free-standing%20showers.">
                Determinations of Home Modifications{" "}
              </a>
            </p>
          </li>
          <li>
            <p>
              <a href="https://udservices.org/services/personal-care-independence/accessible-home-modifications/">
                United Disability Services{" "}
              </a>
            </p>
          </li>
          <li>
            <p>
              <a href="https://liveinplacedesigns.com/home-modification-elderly-and-disabled/">
                Important Home Modifications
              </a>
            </p>
          </li>
          <li>
            <p>
              <a href="https://corporatefinanceinstitute.com/resources/commercial-real-estate/home-modification/">
                Understanding Home Modifications
              </a>
            </p>
          </li>
          <li>
            <p>
              <a href="https://www.moneygeek.com/mortgage/resources/home-modification-for-disability/">
                Making Homes Accessible
              </a>
            </p>
          </li>
          <li>
            <p>
              <a href="https://nasdonline.org/341/d000138/simple-home-modifications-for-the-disabled.html">
                Simple Home Modifications
              </a>
            </p>
          </li>
          <li>
            <p>
              <a href="https://nasdonline.org/341/d000138/simple-home-modifications-for-the-disabled.html">
                Aging in Place
              </a>
            </p>
          </li>
          <li>
            <p>
              <a href="https://www.cerebralpalsy.org/resources/government-resources/housing/home-modifications">
                Modification Standards
              </a>
            </p>
          </li>
          <li>
            <p>
              <a href="https://yourside.org.au/services/home-modifications/">
                Modification for Mobility
              </a>
            </p>
          </li>
          <li>
            <p>
              <a href="https://www.ncbi.nlm.nih.gov/pmc/articles/PMC6604004/">
                National institute of Health
              </a>
            </p>
          </li>
          <li>
            <p>
              <a href="https://www.investopedia.com/terms/h/homemodification.asp">
                How Home Modification Works
              </a>
            </p>
          </li>
          <li>
            <p>
              <a href="https://www.new-horizons.org/houmod.html">
                Modification for Seniors
              </a>
            </p>
          </li>
          <li>
            <p>
              <a href="https://www.new-horizons.org/houmod.html">
                Resources for Home Modification and Safety
              </a>
            </p>
          </li>
          <li>
            <p>
              <a href="https://www.myplacect.org/services-and-supports/assistive-technology-and-home-modifications/home-modifications/">
                Make Your Home Work for Your Needs
              </a>
            </p>
          </li>
          <li>
            <p>
              <a href="https://www.ndis.gov.au/participants/home-and-living/home-modifications-explained">
                Home Modifications Explained
              </a>
            </p>
          </li>
          <li>
            <p>
              <a href="https://www.whaikaha.govt.nz/types-of-support/housing-and-transport/modifying-your-home/">
                Types of Modifications
              </a>
            </p>
          </li>
          <li>
            <p>
              <a href="https://cicoa.org/services/home-accessibility-modifications/">
                Challenges for Old Adults
              </a>
            </p>
          </li>
          <li>
            <p>
              <a href="https://homeguides.sfgate.com/checklist-handicap-home-modifications-101933.html">
                Checklist for Handicapped Home Modifications
              </a>
            </p>
          </li>
          <li>
            <p>
              <a href="https://www.realestateagents.com/real-estate-agents/austin-tx">
                Homes in Austin, Texas
              </a>
            </p>
          </li>
          <li>
            <p>
              <a href="https://focus-ga.org/home-modifications/">
                Access Home Modifications Assistance
              </a>
            </p>
          </li>
          <li>
            <p>
              <a href="https://hcpf.colorado.gov/sites/hcpf/files/Home%20Modification%20Look%20Book-Updated%20July%202017.pdf">
                Modifications Lookbook
              </a>
            </p>
          </li>
          <li>
            <p>
              <a href="https://homeaccess.nationalramp.com/news/home-modifications-for-children-with-disabilities/">
                Modifications for Children with Disabilities
              </a>
            </p>
          </li>
          <li>
            <p>
              <a href="https://www.peoples-law.org/home-modification-self-help-guide">
                Home Modification Self-help Guide
              </a>
            </p>
          </li>
          <li>
            <p>
              <a href="https://www.aafp.org/pubs/afp/issues/2009/1101/p963.html">
                Therapeutic Adaptations
              </a>
            </p>
          </li>
          <li>
            <p>
              <a href="https://www.iaccess.life/home-modifications-for-persons-with-disabilities/">
                The Ultimate Guide for Home Modifications
              </a>
            </p>
          </li>
          <li>
            <p>
              <a href="https://www.seniorlink.com/blog/home-modifications-for-the-elderly">
                Modifications for the Elderly
              </a>
            </p>
          </li>
        </ul>
      </>
    );
  };

  return (
    <>
      <Head
        title={
          "An Accessibility Checklist to Modify Your Home | RealEstateAgents.com"
        }
        description={
          "Making a home more accessible can make the lives of residents much easier. Use this checklist to finds ways to make substantial changes."
        }
      />
      <article className={styles["Article"]}>
        <div className={styles["Article__container"]}>{content()}</div>
      </article>
      <Script strategy="beforeInteractive" type="application/ld+json">
        {{
          "@context": "https://schema.org",
          "@type": "Article",
          mainEntityOfPage: {
            "@type": "WebPage",
            "@id":
              "https://www.realestateagents.com/articles/accessibility-checklist-to-modify-home",
          },
          headline: "An Accessibility Checklist to Modify Your Home",
          author: {
            "@type": "Person",
            name: "Amber Martinez",
            jobTitle: "Education Writer",
            sameAs: [
              "https://www.realestateagents.com/articles/author/amber-martinez",
              "https://www.linkedin.com/in/amber-martinez-7096651a/",
            ],
          },
        }}
      </Script>
    </>
  );
}
